import { useCallback, useEffect, useRef, useState } from 'react';
import { SecurityVulnerability, AiVerificationStatus, JobStatusResponse } from '../types';
import { checkHealth, submitAnalysis, pollJob, streamJob } from '../services/aiVerificationService';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 min — generous margin over the backend's own ~10 min wall-clock cap

const IN_PROGRESS_STATUSES: ReadonlySet<AiVerificationStatus> = new Set<AiVerificationStatus>([
  'submitting', 'fetching', 'scanning', 'indexing', 'verifying',
]);

export interface AiVerificationState {
  available: boolean;
  status: AiVerificationStatus;
  findings: SecurityVulnerability[] | null;
  summary: string | null;
  verificationMessage: string | null;
  error: string | null;
  start: () => void;
}

export function useAiVerification(
  repoUrl: string | undefined,
  githubToken: string | undefined,
  clientFindings: SecurityVulnerability[]
): AiVerificationState {
  const [available, setAvailable] = useState(false);
  const [status, setStatus] = useState<AiVerificationStatus>('idle');
  const [findings, setFindings] = useState<SecurityVulnerability[] | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const statusRef = useRef(status);
  statusRef.current = status;
  const cleanupRef = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    checkHealth().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => () => cleanupRef.current(), []);

  /** The one authoritative read of final job state. Returns true once the
   * job has reached a terminal state (done/error), so callers know to stop
   * polling. */
  const applyJobStatus = useCallback((job: JobStatusResponse): boolean => {
    if (job.status === 'error') {
      setStatus('error');
      setError(job.error || 'Deep analysis failed.');
      return true;
    }
    if (job.status === 'done') {
      setFindings(job.findings);
      setSummary(job.summary);
      setVerificationMessage(job.verificationMessage ?? null);
      setStatus('done');
      return true;
    }
    setStatus(job.status as AiVerificationStatus);
    return false;
  }, []);

  const beginPolling = useCallback(
    (jobId: string) => {
      let stopped = false;
      const startedAt = Date.now();
      const tick = async () => {
        if (stopped) return;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setStatus('error');
          setError('Deep analysis timed out.');
          return;
        }
        try {
          const job = await pollJob(jobId);
          const terminal = applyJobStatus(job);
          if (!terminal && !stopped) {
            const timer = setTimeout(tick, POLL_INTERVAL_MS);
            cleanupRef.current = () => {
              stopped = true;
              clearTimeout(timer);
            };
          }
        } catch (err) {
          setStatus('error');
          setError(err instanceof Error ? err.message : 'Polling failed.');
        }
      };
      tick();
      cleanupRef.current = () => {
        stopped = true;
      };
    },
    [applyJobStatus]
  );

  const start = useCallback(() => {
    if (!repoUrl || IN_PROGRESS_STATUSES.has(statusRef.current)) return;
    setStatus('submitting');
    setError(null);
    setVerificationMessage(null);
    submitAnalysis(repoUrl, clientFindings, githubToken)
      .then(({ jobId }) => {
        const stopStream = streamJob(
          jobId,
          (event) => {
            if (event.type === 'progress') {
              setStatus(event.stage as AiVerificationStatus);
            } else if (event.type === 'done' || event.type === 'error') {
              pollJob(jobId)
                .then(applyJobStatus)
                .catch((err) => {
                  setStatus('error');
                  setError(err instanceof Error ? err.message : 'Failed to fetch final results.');
                });
            }
          },
          () => beginPolling(jobId)
        );
        cleanupRef.current = stopStream;
      })
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Failed to submit deep analysis.');
      });
  }, [repoUrl, githubToken, clientFindings, beginPolling, applyJobStatus]);

  return { available, status, findings, summary, verificationMessage, error, start };
}
